/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState } from "react";
import { Link } from "react-router-dom";
import { signUp, signIn } from "../lib/auth-client";
import {
  sendSignupOtp,
  getSignupVerifyStatus,
  resendSignupVerifyLink,
} from "../lib/api";
import { Icon } from "../components/Common.jsx";
import AuthSide from "../components/AuthSide.jsx";
import AuthThemeToggle from "../components/AuthThemeToggle.jsx";
import WhatsAppVerifyStep from "../components/WhatsAppVerifyStep.jsx";
import PhoneInput from "../components/PhoneInput.jsx";
import PasswordStrength from "../components/PasswordStrength.jsx";
import { meetsMinStrength, passwordGateError } from "../lib/password-utils";
import { detectDefaultCountry } from "../lib/timezone-country";

// Owner / admin sign-up. 3-stage flow:
//   1. 'form'  → user fills companyName, name, email, password, phone.
//                On submit we POST /api/signup-otp/send → receive
//                verificationToken + wa.me link → advance to 'otp'.
//   2. 'otp'   → user taps "Verify on WhatsApp", which opens WhatsApp with
//                "verify <token>" prefilled. Frontend polls verify-status
//                until verified, then calls Better Auth's signUp.email with
//                the verificationToken so the backend hook accepts the
//                signup.
//   3. 'sent'  → standard "check your email to verify your address" screen.
const maskPhone = (raw) => {
  if (!raw) return "";
  const digits = raw.replace(/\D/g, "");
  if (digits.length < 6) return raw;
  const last4 = digits.slice(-4);
  const cc = digits.length > 10 ? digits.slice(0, digits.length - 10) : "";
  return `${cc ? "+" + cc + " " : ""}******${last4}`;
};

export default function SignupPage() {
  const [stage, setStage] = useState("form"); // 'form' | 'otp' | 'sent'

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState(""); // full E.164, e.g. "+919876543210"
  const [phoneValid, setPhoneValid] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  // Pre-select the phone country from the device timezone (synchronous, no
  // network). Falls back to India for unmapped zones. The user can still
  // override via the dropdown.
  const [detectedCountry] = useState(() => detectDefaultCountry());
  const [companyName, setCompanyName] = useState("");
  const [pass, setPass] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Carried from form → otp → final signUp call.
  const [verificationToken, setVerificationToken] = useState("");
  const [normalizedPhone, setNormalizedPhone] = useState(""); // E.164
  const [waLink, setWaLink] = useState("");
  const [botPhone, setBotPhone] = useState("");
  const [verifyMessage, setVerifyMessage] = useState("");
  const [verifyExpiresAt, setVerifyExpiresAt] = useState("");

  const [sentTo, setSentTo] = useState("");

  const onPassChange = (v) => setPass(v);

  const onPhoneChange = ({ e164, isValid, national }) => {
    setPhone(e164);
    setPhoneValid(isValid);
    if (!national) setPhoneError("Phone is required");
    else if (!isValid) setPhoneError("Enter a valid phone number");
    else setPhoneError("");
  };

  // ── Stage 1: form submit → request OTP ────────────────────────────────
  const submitForm = async (e) => {
    e.preventDefault();
    setError("");

    if (!meetsMinStrength(pass)) {
      setError(passwordGateError(pass));
      return;
    }
    if (!agreed) {
      setError("Please agree to the Terms and Privacy Policy.");
      return;
    }
    if (!phone || !phoneValid || phoneError) {
      setError("Please enter a valid phone number.");
      return;
    }

    // `phone` is already full E.164 from PhoneInput — reuse it for OTP send
    // and the eventual signUp.email call.
    setNormalizedPhone(phone);

    setSubmitting(true);
    try {
      const data = await sendSignupOtp(phone);
      setVerificationToken(data.verificationToken);
      setWaLink(data.waLink);
      setBotPhone(data.botPhone);
      setVerifyMessage(data.verifyMessage);
      setVerifyExpiresAt(data.expiresAt);
      setStage("otp");
    } catch (err) {
      setError(err.message || "Could not start phone verification.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Stage 2: phone verified via WhatsApp → call Better Auth signUp.email ──
  const handleVerified = async () => {
    setSubmitting(true);
    try {
      // Referral (#1): carry a ?ref=CODE through to account creation so the
      // new company records who referred it.
      const referredByCode =
        new URLSearchParams(window.location.search).get("ref") || undefined;
      const { error: authError } = await signUp.email({
        email: email.trim(),
        password: pass,
        name: name.trim(),
        companyName: companyName.trim(),
        phone: normalizedPhone,
        verificationToken,
        referredByCode,
        callbackURL: `${window.location.origin}/signin?verified=1`,
      });

      if (authError) {
        setError(authError.message || "Could not create your account.");
        return;
      }

      setSentTo(email.trim());
      setStage("sent");
    } catch (err) {
      setError(err.message || "Could not create your account.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Stage 3: email sent confirmation ──────────────────────────────────
  if (stage === "sent" || sentTo) {
    return (
      <div className="auth-page">
        <AuthThemeToggle />
        <AuthSide
          title="Start your 14-day free trial."
          subtitle="No credit card required. Set up your first WhatsApp report flow in under 5 minutes."
          stats={[
            { v: "14 days", l: "Free trial" },
            { v: "5 min", l: "Setup time" },
            { v: "No card", l: "Required" },
          ]}
          chatVariant="B"
        />
        <div className="auth-form-wrap">
          <div className="auth-form" style={{ textAlign: "center" }}>
            <div
              style={{
                margin: "0 auto 16px",
                width: 56,
                height: 56,
                borderRadius: "50%",
                background: "rgba(124,58,237,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon name="mail" size={26} />
            </div>
            <h1>Check your email</h1>
            <p>
              We sent a verification link to{" "}
              <strong style={{ color: "var(--text)" }}>{sentTo}</strong>.
            </p>
            <p
              style={{
                fontSize: 13,
                color: "var(--text-muted)",
                marginBottom: 18,
              }}
            >
              Phone verified ✓ &nbsp;·&nbsp; Click the link in your inbox to
              finish creating your workspace. The link expires in 1 hour.
            </p>
            <Link
              to="/signin"
              className="btn btn-primary"
              style={{ width: "100%", justifyContent: "center" }}
            >
              Go to sign in <Icon name="arrow-right" size={16} />
            </Link>
            <div className="auth-foot">
              Didn't get it?{" "}
              <a
                href="#"
                onClick={(ev) => {
                  ev.preventDefault();
                  setSentTo("");
                  setStage("form");
                }}
              >
                Try again
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Stage 2 UI: OTP entry ─────────────────────────────────────────────
  if (stage === "otp") {
    return (
      <div className="auth-page">
        <AuthThemeToggle />
        <AuthSide
          title="One quick verification."
          subtitle="We're confirming your phone so the WhatsApp report bot can reach you reliably."
          stats={[
            { v: "10 min", l: "Link valid for" },
            { v: "WhatsApp", l: "Click-to-verify" },
            { v: "1-time", l: "Per signup" },
          ]}
          chatVariant="B"
        />
        <div className="auth-form-wrap">
          <div className="auth-form">
            <h1>Verify your phone</h1>
            {error && (
              <div
                role="alert"
                style={{
                  marginBottom: 16,
                  padding: "10px 14px",
                  borderRadius: 10,
                  fontSize: 13,
                  background: "rgba(239,68,68,0.1)",
                  border: "1px solid rgba(239,68,68,0.4)",
                  color: "#F87171",
                }}
              >
                {error}
              </div>
            )}
            <WhatsAppVerifyStep
              token={verificationToken}
              waLink={waLink}
              botPhone={botPhone}
              verifyMessage={verifyMessage}
              phone={maskPhone(normalizedPhone)}
              expiresAt={verifyExpiresAt}
              onVerified={handleVerified}
              getStatus={getSignupVerifyStatus}
              doResend={resendSignupVerifyLink}
            />
            <div className="auth-foot">
              Wrong number?{" "}
              <a
                href="#"
                onClick={(ev) => {
                  ev.preventDefault();
                  setStage("form");
                }}
              >
                Go back
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Stage 1 UI: full sign-up form ─────────────────────────────────────
  return (
    <div className="auth-page">
      <AuthThemeToggle />
      <AuthSide
        title="Start your 14-day free trial."
        subtitle="No credit card required. Set up your first WhatsApp report flow in under 5 minutes."
        stats={[
          { v: "14 days", l: "Free trial" },
          { v: "5 min", l: "Setup time" },
          { v: "No card", l: "Required" },
        ]}
        chatVariant="B"
      />
      <div className="auth-form-wrap">
        <form className="auth-form" onSubmit={submitForm}>
          <h1>Create your account</h1>
          <p>Get started with ReportFlow free for 14 days</p>

          {error && (
            <div
              role="alert"
              style={{
                marginBottom: 16,
                padding: "10px 14px",
                borderRadius: 10,
                fontSize: 13,
                background: "rgba(239,68,68,0.1)",
                border: "1px solid rgba(239,68,68,0.4)",
                color: "#F87171",
              }}
            >
              {error}
            </div>
          )}

          <button
            type="button"
            className="btn btn-ghost"
            onClick={() =>
              signIn.social({
                provider: "google",
                callbackURL: `${window.location.origin}/complete-signup`,
              })
            }
            style={{
              width: "100%",
              justifyContent: "center",
              gap: 10,
              marginBottom: 16,
            }}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>

          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 18,
            color: "var(--text-muted)",
            fontSize: 12,
          }}>
            <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
            <span>or sign up with email</span>
            <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
          </div>

          <div className="field">
            <label>Full name</label>
            <div className="input-group">
              <Icon name="user" size={16} />
              <input
                type="text"
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Aarav Sharma"
                required
              />
            </div>
          </div>

          <div className="field">
            <label>Work email</label>
            <div className="input-group">
              <Icon name="mail" size={16} />
              <input
                type="email"
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                autoComplete="email"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="field">
              <label>Phone</label>
              <PhoneInput
                onChange={onPhoneChange}
                defaultCountry={detectedCountry}
                error={phoneError}
                disabled={submitting}
              />
            </div>
            <div className="field">
              <label>Company</label>
              <div className="input-group">
                <Icon name="building-2" size={16} />
                <input
                  type="text"
                  className="input"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Acme Pvt Ltd"
                  required
                />
              </div>
            </div>
          </div>

          <div className="field">
            <label>Password</label>
            <div className="input-group">
              <Icon name="lock" size={16} />
              <input
                type={showPass ? "text" : "password"}
                className="input"
                value={pass}
                onChange={(e) => onPassChange(e.target.value)}
                placeholder="At least 8 characters"
                required
                minLength={8}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="input-suffix"
                onClick={() => setShowPass((s) => !s)}
                aria-label={showPass ? "Hide password" : "Show password"}
              >
                <Icon name={showPass ? "eye-off" : "eye"} size={16} />
              </button>
            </div>
            <PasswordStrength password={pass} />
          </div>

          <label className="checkbox" style={{ marginBottom: 18 }}>
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              required
            />
            <span>
              I agree to the <a href="#">Terms</a> and{" "}
              <a href="#">Privacy Policy</a>
            </span>
          </label>

          <button
            className="btn btn-primary"
            type="submit"
            disabled={submitting || !!phoneError || !phoneValid}
            style={{ width: "100%", justifyContent: "center" }}
          >
            {submitting ? (
              "Sending verification code…"
            ) : (
              <>
                Continue <Icon name="arrow-right" size={16} />
              </>
            )}
          </button>

          <div className="auth-foot">
            Already have an account? <Link to="/signin">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
